/* @jsxImportSource react */
'use client';

import {
  type ChangeEvent,
  type FormEvent,
  type FormHTMLAttributes,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import { type AnyFieldApi } from '@tanstack/react-form';
import { useSelector } from '@tanstack/react-store';

import { Button, type ButtonAsButtonProps } from '../button/button';
import {
  Checkbox,
  type CheckboxAnalyticsProps,
  type CheckboxBaseProps,
} from '../checkbox/checkbox';
import { Combobox, type ComboboxOption, type ComboboxProps } from '../combobox/combobox';
import {
  Field,
  fieldLabel,
  FormContext,
  type FormControlBaseProps,
  type FormControlLabelProps,
  useConnectedField,
  useRequiredFormContext,
} from '../field/field';
import { Input, type TextInputProps } from '../input/input';
import { TextArea, type TextAreaProps } from '../input/text-area';
import { FormInputPhone } from '../phone-input/phone-input';
import { Radio, type RadioGroupProps } from '../radio/radio';

/** @internal */
export const ATOMS_FORM_VARIANTS = {} as const;
/** @internal */
export const ATOMS_FORM_DEFAULT_VARIANTS = null;

/** Loose form-instance type — accepts any specific TanStack `FormApi`. */
type AnyFormApi = AnyFieldApi['form'];

// ---------- Input -------------------------------------------------------------

/** @public */
export type FormInputProps = FormControlBaseProps &
  FormControlLabelProps &
  Omit<
    TextInputProps,
    'name' | 'value' | 'onChange' | 'onBlur' | 'error' | 'aria-label' | 'aria-labelledby'
  >;

function FormInput({
  name,
  label,
  'aria-label': ariaLabel,
  description,
  required,
  id,
  ...inputProps
}: FormInputProps) {
  return (
    <Field
      name={name}
      label={fieldLabel(label, ariaLabel)}
      description={description}
      required={required}
      id={id}
    >
      {(f) => (
        <Input
          {...inputProps}
          // `f.id` matches the Field label's `htmlFor`, associating the visible
          // label with the input (and satisfying Input's accessible-name type).
          id={f.id}
          name={f.name}
          value={typeof f.value === 'string' ? f.value : ''}
          // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop -- must map the event to e.target.value; this runs inside Field's render prop where no hook can hoist it, and Input isn't memoized so the fresh identity is harmless
          onChange={(e: ChangeEvent<HTMLInputElement>) => f.setValue(e.target.value)}
          onBlur={f.onBlur}
          error={f.hasError}
        />
      )}
    </Field>
  );
}

FormInput.displayName = 'Form.Input';

// ---------- TextArea ---------------------------------------------------------

/** @public */
export type FormTextAreaProps = FormControlBaseProps &
  FormControlLabelProps &
  Omit<
    TextAreaProps,
    | 'name'
    | 'value'
    | 'onChange'
    | 'onValueChange'
    | 'onBlur'
    | 'error'
    | 'aria-label'
    | 'aria-labelledby'
  >;

function FormTextArea({
  name,
  label,
  'aria-label': ariaLabel,
  description,
  required,
  id,
  ...textareaProps
}: FormTextAreaProps) {
  return (
    <Field
      name={name}
      label={fieldLabel(label, ariaLabel)}
      description={description}
      required={required}
      id={id}
    >
      {(f) => (
        <TextArea
          {...textareaProps}
          // `f.id` matches the Field label's `htmlFor` for label association.
          id={f.id}
          name={f.name}
          value={typeof f.value === 'string' ? f.value : ''}
          onValueChange={f.setValue}
          onBlur={f.onBlur}
          error={f.hasError}
        />
      )}
    </Field>
  );
}

FormTextArea.displayName = 'Form.TextArea';

// ---------- Combobox ---------------------------------------------------------

/** @public */
export type FormComboboxProps = FormControlBaseProps &
  FormControlLabelProps &
  Omit<
    ComboboxProps,
    'name' | 'value' | 'defaultValue' | 'onValueChange' | 'error' | 'aria-label' | 'aria-labelledby'
  >;

// The field stores its value as `unknown`; narrow it structurally to the
// combobox's option shape before handing it back to the control.
function isComboboxOption(value: unknown): value is ComboboxOption {
  return (
    typeof value === 'object' &&
    value !== null &&
    'value' in value &&
    typeof value.value === 'string' &&
    'label' in value &&
    typeof value.label === 'string'
  );
}

function FormCombobox({
  name,
  label,
  'aria-label': ariaLabel,
  description,
  required,
  id,
  ...comboboxProps
}: FormComboboxProps) {
  return (
    <Field
      name={name}
      label={fieldLabel(label, ariaLabel)}
      description={description}
      required={required}
      id={id}
    >
      {(f) => (
        <Combobox
          {...comboboxProps}
          // `f.id` matches the Field label's `htmlFor`, associating the visible
          // label with the combobox input.
          id={f.id}
          name={f.name}
          value={isComboboxOption(f.value) ? f.value : null}
          onValueChange={f.setValue}
          error={f.hasError}
        />
      )}
    </Field>
  );
}

FormCombobox.displayName = 'Form.Combobox';

// ---------- Checkbox ---------------------------------------------------------

// Field renders `label` + `description` above the row (like the other bindings)
// and the error below; Checkbox itself renders the `caption` to the right of the
// box (as a sibling `<label htmlFor>`, so clicking the text toggles it). This
// binding just wires the two together: `f.id` ties Field's label association to
// the control, and `variant` is derived from the field's error state, so it
// isn't forwarded.

/**
 * Checkbox name requirement: like {@link FormControlLabelProps}, but the
 * checkbox's own `caption` also counts — it renders as a `<label htmlFor>`
 * that names the control.
 */
type FormCheckboxLabelProps =
  | { label: ReactNode; caption?: ReactNode; 'aria-label'?: undefined }
  | { label?: undefined; caption: ReactNode; 'aria-label'?: undefined }
  | { label?: undefined; caption?: ReactNode; 'aria-label': string };

/** @public */
export type FormCheckboxProps = FormControlBaseProps &
  FormCheckboxLabelProps &
  Omit<CheckboxBaseProps, 'checked' | 'onCheckedChange' | 'onBlur' | 'name' | 'value' | 'variant'> &
  CheckboxAnalyticsProps & {
    /** DOM id for the control (generated when omitted). */
    id?: string;
  };

function FormCheckbox({
  name,
  label,
  'aria-label': ariaLabel,
  caption,
  description,
  required,
  id,
  ...checkboxProps
}: FormCheckboxProps) {
  // When the checkbox supplies its own inline label (`caption`), that caption is
  // the control's accessible name, so render the field `label` as a non-associated
  // heading — otherwise both would point at the same control and the name would
  // double ("Terms of service I agree to the terms"). Without a caption, the field
  // label is the control's label as usual.
  const labelMode = caption != null ? 'heading' : 'control';
  return (
    <Field
      name={name}
      label={fieldLabel(label, ariaLabel)}
      labelMode={labelMode}
      description={description}
      required={required}
      id={id}
    >
      {(f) => (
        <Checkbox
          {...checkboxProps}
          caption={caption}
          id={f.id}
          name={f.name}
          checked={Boolean(f.value)}
          onCheckedChange={f.setValue}
          onBlur={f.onBlur}
          variant={f.hasError ? 'error' : 'default'}
        />
      )}
    </Field>
  );
}

FormCheckbox.displayName = 'Form.Checkbox';

// ---------- Radio ------------------------------------------------------------

// Unlike the other bindings, `Radio.Group` renders its own `Field` (label,
// description, error) — so this binding does NOT wrap it in a connected `Field`
// (that would nest two Fields and break the group's label association). Instead
// it subscribes to the form via `useConnectedField` and feeds the group's own
// chrome props directly.
/** @public */
export type FormRadioProps = FormControlBaseProps &
  FormControlLabelProps &
  Omit<
    RadioGroupProps,
    | 'label'
    | 'description'
    | 'required'
    | 'name'
    | 'value'
    | 'onValueChange'
    | 'onBlur'
    | 'error'
    | 'invalid'
  >;

function FormRadio({
  name,
  label,
  'aria-label': ariaLabel,
  description,
  required,
  children,
  ...radioProps
}: FormRadioProps) {
  const f = useConnectedField(name, { required });
  return (
    <Radio.Group
      {...radioProps}
      label={fieldLabel(label, ariaLabel)}
      description={description}
      required={f.isRequired}
      name={f.name}
      value={typeof f.value === 'string' ? f.value : undefined}
      onValueChange={f.setValue}
      onBlur={f.onBlur}
      // The group renders a single message; surface the first error. `invalid`
      // needs no explicit wiring — the group derives it from `error`.
      error={f.errors[0]}
    >
      {children}
    </Radio.Group>
  );
}

FormRadio.displayName = 'Form.Radio';

// ---------- SubmitButton -----------------------------------------------------

// A submit button is always a native <button>, so base its props on the button
// form. Omitting over the discriminated `ButtonProps` union would keep only the
// keys common to both arms and widen handler element types — base on the single
// arm instead.
/** @public */
export type FormSubmitButtonProps = Omit<
  ButtonAsButtonProps,
  'type' | 'onClick' | 'loading' | 'href'
>;

function FormSubmitButton({ children = 'Submit', ...buttonProps }: FormSubmitButtonProps) {
  const form = useRequiredFormContext();
  // Reflect the form's in-flight state as the button's loading state, so the
  // button shows a spinner (and blocks re-submits) while `onSubmit` runs.
  const isSubmitting = useSelector(form.store, (state) => state.isSubmitting);
  return (
    <Button {...buttonProps} type="submit" loading={isSubmitting}>
      {children}
    </Button>
  );
}

FormSubmitButton.displayName = 'Form.SubmitButton';

// ---------- Root -------------------------------------------------------------

export type FormRootProps = Omit<FormHTMLAttributes<HTMLFormElement>, 'onSubmit'> & {
  /** The TanStack form instance returned from `useForm`. */
  form: AnyFormApi;
  /**
   * Whether to scroll to (and focus) the topmost invalid field after a failed
   * submit attempt. Looks for the first `[aria-invalid="true"]` descendant.
   * @default true
   */
  scrollToFirstError?: boolean;
  /** Optional extra handler invoked after the form's own submit handling. */
  onSubmit?: (e: FormEvent<HTMLFormElement>) => void;
};

function FormRoot({
  form,
  scrollToFirstError = true,
  onSubmit,
  children,
  ...formProps
}: FormRootProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const submissionAttempts = useSelector(form.store, (state) => state.submissionAttempts);

  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      form.handleSubmit().catch((err: unknown) => {
        // reportError dispatches an `error` event on the global scope, which is
        // exactly what Sentry's GlobalHandlers integration hooks — more direct
        // than rethrowing via setTimeout and it doesn't perturb the task queue.
        reportError(err);
      });
      onSubmit?.(e);
    },
    [form, onSubmit],
  );

  useEffect(() => {
    if (!scrollToFirstError || submissionAttempts === 0) return;
    // `submissionAttempts` increments before validators run and before React
    // commits the resulting `aria-invalid` to the DOM, so defer the query by a
    // frame to read the post-validation, post-commit state.
    requestAnimationFrame(() => {
      const el = formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]');
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // For Input/Combobox the invalid element is itself the focusable control,
      // but for a Radio.Group `aria-invalid` lands on the non-focusable group
      // wrapper — focus its first focusable descendant so keyboard focus actually
      // moves into the offending control rather than just scrolling to it.
      const focusableSelector =
        'input, select, textarea, button, a[href], [tabindex]:not([tabindex="-1"])';
      const focusTarget = el.matches(focusableSelector)
        ? el
        : (el.querySelector<HTMLElement>(focusableSelector) ?? el);
      // `preventScroll` so focusing the field doesn't trigger an instant
      // jump-scroll that would override the smooth `scrollIntoView` animation.
      focusTarget.focus({ preventScroll: true });
    });
  }, [submissionAttempts, scrollToFirstError]);

  return (
    <FormContext.Provider value={form}>
      <form {...formProps} ref={formRef} onSubmit={handleSubmit}>
        {children}
      </form>
    </FormContext.Provider>
  );
}

FormRoot.displayName = 'Form.Root';

/**
 * TanStack Form bindings for atoms inputs. Wrap your fields in `<Form.Root>` and
 * the input components subscribe to the form by `name` — no `<form.Field>`
 * render-prop boilerplate required. Each is a thin per-type binding over the
 * connected `Field`, which owns label, the schema-derived required state, and
 * error display.
 *
 * Default error-display UX: errors stay hidden until the user presses submit for
 * the first time, then render below each control and update live as the user
 * fixes (or breaks) validation — use a form-level `validators.onChange` schema
 * so all fields validate on every keystroke.
 *
 * @example
 * ```tsx
 * const form = useForm({
 *   defaultValues: { email: '' },
 *   validators: { onChange: z.object({ email: z.string().email() }) },
 *   onSubmit: ({ value }) => submitToApi(value),
 * });
 *
 * <Form.Root form={form}>
 *   <Form.Input name="email" label="Email" type="email" />
 *   <Button aria-label="Submit" analyticsName="app-submit" type="submit">Submit</Button>
 * </Form.Root>;
 * ```
 */
export const Form = {
  Root: FormRoot,
  Input: FormInput,
  InputPhone: FormInputPhone,
  TextArea: FormTextArea,
  Combobox: FormCombobox,
  Checkbox: FormCheckbox,
  Radio: FormRadio,
  SubmitButton: FormSubmitButton,
};
